/* eslint-disable import/prefer-default-export */
import dayjs from 'dayjs'
import { isBoolean, isEmpty } from 'lodash'

import { ReleaseNoteFragment } from '~generated/operations'
import { ReleaseNotesFilterInput } from '~generated/types'

type Filter = {
  isApplied: (item: ReleaseNoteFragment) => boolean
  isEmpty: () => boolean
}

export function getApplicableFilters(filter: ReleaseNotesFilterInput): Filter[] {
  const queryRegExp = filter.query ? new RegExp(filter.query, 'i') : null

  const filterInstance: Record<keyof ReleaseNotesFilterInput, Filter> = {
    query: {
      isApplied: (item) => {
        if (!queryRegExp) return true
        return queryRegExp.test(item.name)
      },

      isEmpty: () => {
        return !filter.query
      },
    },

    tagIds: {
      isApplied: (item) => {
        return !!item.tags?.some((tag) => filter.tagIds.includes(tag.id))
      },

      isEmpty: () => {
        return isEmpty(filter.tagIds)
      },
    },

    ownerIds: {
      isApplied: (item) => {
        return filter.ownerIds.includes(item.owner?.id)
      },

      isEmpty: () => {
        return isEmpty(filter.ownerIds)
      },
    },

    launchIds: {
      isApplied: (item) => {
        return filter.launchIds.includes(item.launch?.id)
      },

      isEmpty: () => {
        return isEmpty(filter.launchIds)
      },
    },

    startDueDate: {
      isApplied: (item) => {
        if (!item.dueDate) return false
        if (!filter.startDueDate) return true
        return dayjs(item.dueDate).valueOf() >= dayjs(filter.startDueDate).valueOf()
      },

      isEmpty: () => {
        return !filter.startDueDate
      },
    },

    endDueDate: {
      isApplied: (item) => {
        if (!item.dueDate) return false
        if (!filter.endDueDate) return true
        return dayjs(item.dueDate).valueOf() <= dayjs(filter.endDueDate).valueOf()
      },

      isEmpty: () => {
        return !filter.endDueDate
      },
    },

    dueDateExists: {
      isApplied: (item) => {
        return !!item.dueDate
      },

      isEmpty: () => {
        return !isBoolean(filter.dueDateExists)
      },
    },

    live: {
      isApplied: (item) => {
        return item.dueDate && dayjs(item.dueDate).isSame(new Date(), 'day')
      },

      isEmpty: () => {
        return !isBoolean(filter.live)
      },
    },

    internal: {
      isApplied: (item) => {
        return item.internal === true
      },

      isEmpty: () => {
        return !isBoolean(filter.internal)
      },
    },
  }

  return Object.keys(filter).reduce<Filter[]>((acc, key) => {
    if (filterInstance[key] && !filterInstance[key].isEmpty()) acc.push(filterInstance[key])
    return acc
  }, [])
}

export type FilterReleaseNotesParams = {
  items: ReleaseNoteFragment[]
  filter: ReleaseNotesFilterInput
}

export function filterReleaseNote({ items, filter = {} }: FilterReleaseNotesParams) {
  const applicableFilters = getApplicableFilters(filter)

  return items.filter((item) => {
    return applicableFilters.every((filter) => filter.isApplied(item))
  })
}
